import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Lock, Globe, Users, Loader2, AlertCircle, Heart } from "lucide-react";
import { Button } from "@tabipla/ui/components/button";
import { Input } from "@tabipla/ui/components/input";
import { Textarea } from "@tabipla/ui/components/textarea";
import { toast } from "sonner";
import { MekkeLayout } from "../layouts/MekkeLayout";
import {
  useBooklet,
  useCreateBooklet,
  useUpdateBooklet,
} from "../../application/hooks/useBooklets";
import { cn } from "@tabipla/ui/lib/utils";
import type { MekkeBookletVisibility } from "@tabipla/shared";

export function BookletFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  // Fetch existing booklet data if editing
  const { data: existingBooklet, isLoading: isLoadingBooklet } = useBooklet(id);
  const createMutation = useCreateBooklet();
  const updateMutation = useUpdateBooklet(id || "");

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<MekkeBookletVisibility>("private");

  // Load existing booklet data when editing
  useEffect(() => {
    if (existingBooklet) {
      setTitle(existingBooklet.title);
      setDescription(existingBooklet.description || "");
      setVisibility(existingBooklet.visibility);
    }
  }, [existingBooklet]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error(t("booklets.titleRequired"));
      return;
    }

    const bookletData = {
      title: title.trim(),
      description: description.trim() || undefined,
      visibility,
    };

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(bookletData);
        toast.success(t("booklets.updated"));
      } else {
        const result = await createMutation.mutateAsync(bookletData);
        toast.success(t("booklets.created"));
        navigate(`/booklets/${result?.id}`);
        return;
      }
      navigate(-1);
    } catch {
      toast.error(t("common.error"));
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isEditing && isLoadingBooklet) {
    return (
      <MekkeLayout hideNav>
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MekkeLayout>
    );
  }

  if (isEditing && !existingBooklet && !isLoadingBooklet) {
    return (
      <MekkeLayout hideNav>
        <div className="flex min-h-screen flex-col items-center justify-center px-4">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-10 w-10 text-destructive" />
          </div>
          <h2 className="mb-2 text-lg font-semibold">{t("common.error")}</h2>
          <p className="mb-6 text-center text-sm text-muted-foreground">
            {t("common.unknownError")}
          </p>
          <Button variant="outline" onClick={() => navigate("/booklets")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("common.back")}
          </Button>
        </div>
      </MekkeLayout>
    );
  }

  const visibilityOptions = [
    {
      id: "private" as const,
      icon: Lock,
      labelKey: "booklets.visibility.private",
      description: "booklets.visibility.privateDesc",
    },
    {
      id: "followers_only" as const,
      icon: Heart,
      labelKey: "booklets.visibility.followersOnly",
      description: "booklets.visibility.followersOnlyDesc",
    },
    {
      id: "limited" as const,
      icon: Users,
      labelKey: "booklets.visibility.limited",
      description: "booklets.visibility.limitedDesc",
    },
    {
      id: "public" as const,
      icon: Globe,
      labelKey: "booklets.visibility.public",
      description: "booklets.visibility.publicDesc",
    },
  ];

  return (
    <MekkeLayout hideNav>
      <div className="min-h-screen pb-24">
        {/* Header */}
        <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("common.cancel")}
            </Button>
            <h1 className="text-lg font-semibold">
              {isEditing ? t("booklets.edit") : t("booklets.create")}
            </h1>
            <Button size="sm" onClick={handleSubmit} disabled={isSubmitting || !title.trim()}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
            </Button>
          </div>
        </header>

        {/* Form */}
        <div className="mx-auto max-w-4xl px-4 py-6">
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t("booklets.name")} <span className="text-destructive">*</span>
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("booklets.name")}
              />
            </div>

            {/* Description */}
            <div>
              <label className="mb-2 block text-sm font-medium">{t("booklets.description")}</label>
              <Textarea
                value={description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setDescription(e.target.value)
                }
                placeholder={t("booklets.description")}
                rows={4}
              />
            </div>

            {/* Visibility */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t("booklets.visibility.label")}
              </label>
              <div className="space-y-2">
                {visibilityOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = visibility === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setVisibility(option.id)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-colors",
                        isSelected ? "border-primary bg-primary/5" : "border-border hover:bg-muted"
                      )}
                    >
                      <div
                        className={cn(
                          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                          isSelected
                            ? "bg-primary/10 text-primary"
                            : "bg-muted text-muted-foreground"
                        )}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className={cn("font-medium", isSelected && "text-primary")}>
                          {t(option.labelKey)}
                        </h3>
                        <p className="text-sm text-muted-foreground">{t(option.description)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MekkeLayout>
  );
}
